import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button, ConfigProvider, Dropdown, Input, Segmented, Select, Spin, Table, Tag, Tooltip, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  AppstoreOutlined,
  AudioOutlined,
  DownOutlined,
  FileTextOutlined,
  LoginOutlined,
  PictureOutlined,
  UpOutlined,
  VideoCameraOutlined,
} from "@ant-design/icons";
import { useI18n } from "../i18n";
import { useTheme } from "../useTheme";
import { buildTheme } from "../theme";
import { vendorColor } from "../vendorColors";
import { fetchDirectory, modalitiesOf, type DirectoryItem, type DirectoryState } from "./directory";
import { CAP_META, formatContext, formatPrice } from "./modelCatalog";
import { VendorAvatar } from "./VendorAvatar";
import { VendorLogo } from "./VendorLogo";
import { MarketingNav } from "./MarketingNav";
import { MarketingFooter } from "./MarketingFooter";
import "./marketing.css";

const { Title, Paragraph, Text } = Typography;
const { Search } = Input;

const MODALITY_TABS = [
  { value: "all", icon: <AppstoreOutlined />, zh: "全部", en: "All" },
  { value: "text", icon: <FileTextOutlined />, zh: "文字", en: "Text" },
  { value: "image", icon: <PictureOutlined />, zh: "图像", en: "Image" },
  { value: "audio", icon: <AudioOutlined />, zh: "语音", en: "Audio" },
  { value: "video", icon: <VideoCameraOutlined />, zh: "视频", en: "Video" },
] as const;

type SortKey = "name" | "vendor";

/** Vendors shown before the expand toggle — the rail stays compact
    without inner scrollbars; the rest is disclosed on demand. */
const VENDOR_PREVIEW_COUNT = 8;

export function PublicModelsPage() {
  const { locale } = useI18n();
  const { resolved } = useTheme();
  const isDark = resolved === "dark";

  const [items, setItems] = useState<DirectoryItem[]>([]);
  const [state, setState] = useState<DirectoryState>("loading");
  const [modalities, setModalities] = useState<string[]>([]);
  const [vendor, setVendor] = useState("");
  const [sort, setSort] = useState<SortKey>("name");
  const [vendorsExpanded, setVendorsExpanded] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    let cancelled = false;
    fetchDirectory().then((result) => {
      if (cancelled) return;
      setItems(result.items);
      setState(result.state);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  /* Distinct vendors with counts — the user-facing filter axis. The
     internal providers list is never surfaced on this page. Models the
     metadata catalog cannot classify stay outside the vendor list (they
     remain reachable via 全部/All). */
  const vendorList = useMemo(() => {
    const counts = new Map<string, number>();
    for (const m of items) {
      if (m.vendor === "") continue;
      counts.set(m.vendor, (counts.get(m.vendor) ?? 0) + 1);
    }
    return [...counts.entries()]
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  }, [items]);

  /* Progressive disclosure: preview the busiest vendors, expand for the
     tail. Selecting a vendor outside the preview auto-expands so an
     active filter is never hidden. */
  const previewVendors = vendorList.slice(0, VENDOR_PREVIEW_COUNT);
  const hiddenVendorCount = Math.max(vendorList.length - previewVendors.length, 0);
  const vendorsShown =
    vendorsExpanded || (vendor !== "" && !previewVendors.some((v) => v.name === vendor));
  const visibleVendors = vendorsShown ? vendorList : previewVendors;

  const filtered = useMemo(() => {
    let rows = items;
    if (search) {
      const q = search.toLowerCase();
      rows = rows.filter(
        (m) =>
          m.model_name.toLowerCase().includes(q) ||
          m.display_name.toLowerCase().includes(q) ||
          m.vendor.toLowerCase().includes(q) ||
          m.tags.some((tag) => tag.toLowerCase().includes(q)),
      );
    }
    if (modalities.length > 0) {
      rows = rows.filter((m) => modalitiesOf(m.capabilities).some((bucket) => modalities.includes(bucket)));
    }
    if (vendor) {
      rows = rows.filter((m) => m.vendor === vendor);
    }
    const sorted = [...rows];
    sorted.sort((a, b) => {
      if (sort === "vendor") {
        // Unclassified models sort last instead of first ("" < letters).
        if (a.vendor === "" || b.vendor === "") return a.vendor === b.vendor ? 0 : a.vendor === "" ? 1 : -1;
        return a.vendor.localeCompare(b.vendor) || a.display_name.localeCompare(b.display_name);
      }
      return a.display_name.localeCompare(b.display_name);
    });
    return sorted;
  }, [items, search, modalities, vendor, sort]);

  const toggleModality = (value: string) => {
    setModalities((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
    );
  };

  /* Per-bucket counts for the rail (from all items, so numbers stay
     stable while filtering). */
  const modalityCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const m of items) {
      for (const bucket of modalitiesOf(m.capabilities)) {
        counts.set(bucket, (counts.get(bucket) ?? 0) + 1);
      }
    }
    return counts;
  }, [items]);

  const columns: ColumnsType<DirectoryItem> = [
    {
      title: locale === "zh-CN" ? "模型" : "Model",
      key: "model",
      render: (_, r) => (
        <div className="mk-table-model">
          <VendorAvatar vendor={r.vendor} />
          <div className="mk-table-model__name">
            <Text strong style={{ fontSize: 13 }}>{r.display_name}</Text>
            <span className="mk-table-model__id">{r.model_name}</span>
          </div>
        </div>
      ),
    },
    {
      title: locale === "zh-CN" ? "厂商" : "Vendor",
      key: "vendor",
      width: 130,
      render: (_, r) => (
        <span className="mk-vendor-cell">
          <span
            className="mk-vendor-dot"
            style={{ background: r.vendor ? vendorColor(r.vendor) : "var(--as-muted)" }}
            aria-hidden="true"
          />
          {r.vendor || (locale === "zh-CN" ? "未知" : "Unknown")}
        </span>
      ),
    },
    {
      title: locale === "zh-CN" ? "上下文" : "Context",
      key: "context",
      width: 88,
      align: "right",
      render: (_, r) => (
        <Tooltip title={locale === "zh-CN" ? "模型上下文窗口长度（tokens）" : "Model context window (tokens)"}>
          <span className={`mk-table-num${r.context_tokens ? "" : " mk-table-num--empty"}`}>
            {formatContext(r.context_tokens)}
          </span>
        </Tooltip>
      ),
    },
    {
      title: locale === "zh-CN" ? "输入 /1M" : "Input /1M",
      key: "input",
      width: 96,
      align: "right",
      render: (_, r) =>
        r.input_price == null ? (
          <span className="mk-table-num mk-table-num--empty">—</span>
        ) : (
          <Tooltip title={locale === "zh-CN" ? "每 100 万输入 tokens · 多通道最低价" : "per 1M input tokens · lowest across providers"}>
            <span className="mk-table-num">{formatPrice(r.input_price)}</span>
          </Tooltip>
        ),
    },
    {
      title: locale === "zh-CN" ? "输出 /1M" : "Output /1M",
      key: "output",
      width: 96,
      align: "right",
      render: (_, r) =>
        r.output_price == null ? (
          <span className="mk-table-num mk-table-num--empty">—</span>
        ) : (
          <Tooltip title={locale === "zh-CN" ? "每 100 万输出 tokens · 多通道最低价" : "per 1M output tokens · lowest across providers"}>
            <span className="mk-table-num">{formatPrice(r.output_price)}</span>
          </Tooltip>
        ),
    },
    {
      title: locale === "zh-CN" ? "能力" : "Capabilities",
      key: "capabilities",
      width: 170,
      render: (_, r) => {
        if (r.capabilities.length === 0) {
          return <span className="mk-table-num mk-table-num--empty">—</span>;
        }
        const shown = r.capabilities.slice(0, 4);
        const rest = r.capabilities.length - shown.length;
        return (
          <div className="mk-caps">
            {shown.map((cap) => {
              const meta = CAP_META[cap.toLowerCase()];
              return meta ? (
                <Tooltip key={cap} title={meta.hint}>
                  <span className="mk-cap">
                    {meta.icon}
                    <span>{locale === "zh-CN" ? meta.zh : meta.en}</span>
                  </span>
                </Tooltip>
              ) : (
                <Tooltip key={cap} title={cap}>
                  <span className="mk-cap mk-cap--raw">{cap}</span>
                </Tooltip>
              );
            })}
            {rest > 0 && (
              <Tooltip title={r.capabilities.slice(4).join(", ")}>
                <span className="mk-cap mk-cap--more">+{rest}</span>
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      title: locale === "zh-CN" ? "协议" : "Protocols",
      key: "protocols",
      width: 140,
      render: (_, r) =>
        r.protocols.length === 0 ? (
          <span className="mk-table-num mk-table-num--empty">—</span>
        ) : (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
            {r.protocols.map((p) => (
              <Tag key={p} className="mk-tag mk-tag--accent">{p}</Tag>
            ))}
          </div>
        ),
    },
  ];

  const showEmptyState = state !== "loading" && filtered.length === 0 && items.length === 0;

  const modalityMenu = {
    selectable: true,
    multiple: true,
    selectedKeys: modalities,
    onClick: ({ key }: { key: string }) => toggleModality(key),
    items: MODALITY_TABS.filter((m) => m.value !== "all").map((m) => ({
      key: m.value,
      label: (
        <span className="mk-seg-option">
          {m.icon}
          <span>{locale === "zh-CN" ? m.zh : m.en}</span>
        </span>
      ),
    })),
  };

  return (
    <ConfigProvider theme={buildTheme(isDark)}>
      <div className="mk-page">
        <MarketingNav />

        <div className="mk-container mk-models-layout">
          {/* ── Filter rail (desktop; collapses into the toolbar below 1024px) ── */}
          <nav className="mk-filter" aria-label={locale === "zh-CN" ? "筛选模型" : "Filter models"}>
            <span className="mk-filter__title">{locale === "zh-CN" ? "模态" : "Modality"}</span>
            {MODALITY_TABS.map((m) => {
              const count = m.value === "all" ? items.length : modalityCounts.get(m.value) ?? 0;
              /* Hide buckets the catalog cannot fill — advertising an
                 empty modality is noise, not a filter. */
              if (m.value !== "all" && state === "ok" && items.length > 0 && count === 0) return null;
              const active = m.value === "all" ? modalities.length === 0 : modalities.includes(m.value);
              return (
                <button
                  key={m.value}
                  type="button"
                  className={`mk-filter__item${active ? " mk-filter__item--active" : ""}`}
                  aria-pressed={active}
                  onClick={() => (m.value === "all" ? setModalities([]) : toggleModality(m.value))}
                >
                  {m.icon}
                  <span className="mk-filter__name">{locale === "zh-CN" ? m.zh : m.en}</span>
                  <span className="mk-filter__count">{count}</span>
                </button>
              );
            })}

            <span className="mk-filter__title mk-filter__title--group">
              {locale === "zh-CN" ? "厂商" : "Vendor"}
            </span>
            <div className="mk-filter__scroll">
              <button
                type="button"
                className={`mk-filter__item${vendor === "" ? " mk-filter__item--active" : ""}`}
                aria-pressed={vendor === ""}
                onClick={() => setVendor("")}
              >
                <AppstoreOutlined />
                <span className="mk-filter__name">{locale === "zh-CN" ? "全部厂商" : "All vendors"}</span>
                <span className="mk-filter__count">{items.length}</span>
              </button>
              {visibleVendors.map((v) => (
                <button
                  key={v.name}
                  type="button"
                  className={`mk-filter__item${vendor === v.name ? " mk-filter__item--active" : ""}`}
                  aria-pressed={vendor === v.name}
                  onClick={() => setVendor(vendor === v.name ? "" : v.name)}
                >
                  <VendorLogo vendor={v.name} size={18} />
                  <span className="mk-filter__name">{v.name}</span>
                  <span className="mk-filter__count">{v.count}</span>
                </button>
              ))}
              {hiddenVendorCount > 0 && (
                <button
                  type="button"
                  className="mk-filter__more"
                  aria-expanded={vendorsShown}
                  onClick={() => setVendorsExpanded(!vendorsShown)}
                >
                  {vendorsShown
                    ? (locale === "zh-CN" ? "收起厂商" : "Show fewer vendors")
                    : (
                      <>
                        {locale === "zh-CN" ? "更多厂商" : "More vendors"}
                        <span className="mk-filter__more-count">{hiddenVendorCount}</span>
                      </>
                    )}
                  {vendorsShown ? <UpOutlined /> : <DownOutlined />}
                </button>
              )}
            </div>
          </nav>

          {/* ── Main column ── */}
          <div className="mk-models-main">
            <div className="mk-toolbar">
              {/* Multi-select modality — rail buttons on desktop, dropdown
                  on narrow screens (CSS decides which renders). */}
              <Dropdown menu={modalityMenu} trigger={["click"]} placement="bottomLeft">
                <Button className="mk-toolbar__modality" icon={<AppstoreOutlined />}>
                  {locale === "zh-CN" ? "模态" : "Modality"}
                  {modalities.length > 0 && (
                    <span className="mk-toolbar__modality-count">{modalities.length}</span>
                  )}
                  <DownOutlined style={{ fontSize: 10 }} />
                </Button>
              </Dropdown>

              <Select
                className="mk-toolbar__vendor"
                value={vendor || undefined}
                onChange={(v) => setVendor(v ?? "")}
                allowClear
                placeholder={locale === "zh-CN" ? "全部厂商" : "All vendors"}
                options={vendorList.map((v) => ({
                  value: v.name,
                  label: `${v.name} (${v.count})`,
                }))}
              />

              <Segmented
                value={sort}
                onChange={(v) => setSort(v as SortKey)}
                options={[
                  { value: "name", label: locale === "zh-CN" ? "名称" : "Name" },
                  { value: "vendor", label: locale === "zh-CN" ? "厂商" : "Vendor" },
                ]}
              />

              <Search
                className="mk-toolbar__search"
                placeholder={locale === "zh-CN" ? "搜索模型或厂商" : "Search models or vendors"}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                allowClear
                disabled={state !== "ok" || items.length === 0}
              />
            </div>

            {state === "loading" && (
              <div className="mk-empty">
                <Spin />
              </div>
            )}

            {state === "error" && (
              <div className="mk-empty">
                <Title level={4} style={{ color: "var(--as-ink)", marginBottom: 8 }}>
                  {locale === "zh-CN" ? "模型目录暂时不可用" : "Model catalog is temporarily unavailable"}
                </Title>
                <Paragraph className="mk-sub" style={{ maxWidth: 460, marginInline: "auto", marginBottom: 20 }}>
                  {locale === "zh-CN"
                    ? "请求失败，请稍后重试；问题持续出现时可在控制台用请求 ID 排查。"
                    : "The request failed. Retry shortly, or trace it by request ID in the dashboard if it persists."}
                </Paragraph>
                <button type="button" className="mk-btn mk-btn--primary" onClick={() => { setState("loading"); void fetchDirectory().then((r) => { setItems(r.items); setState(r.state); }); }}>
                  {locale === "zh-CN" ? "重试" : "Retry"}
                </button>
              </div>
            )}

            {state !== "loading" && state !== "error" && showEmptyState && (
              <div className="mk-empty">
                <Title level={4} style={{ color: "var(--as-ink)", marginBottom: 8 }}>
                  {locale === "zh-CN" ? "模型目录将在配置通道后显示" : "Model directory populates once channels are configured"}
                </Title>
                <Paragraph className="mk-sub" style={{ maxWidth: 460, marginInline: "auto", marginBottom: 20 }}>
                  {state === "unauthorized"
                    ? locale === "zh-CN"
                      ? "登录后即可查看当前组织下已接入的模型。"
                      : "Sign in to see the models configured for your organization."
                    : locale === "zh-CN"
                      ? "尚未配置任何通道，配置完成后模型将出现在这里。"
                      : "No channels are configured yet. Models will appear here once you add one."}
                </Paragraph>
                {state === "unauthorized" ? (
                  <Link to="/login" className="mk-btn mk-btn--primary">
                    <LoginOutlined style={{ marginRight: 6 }} />
                    {locale === "zh-CN" ? "登录" : "Sign in"}
                  </Link>
                ) : (
                  <Link to="/dashboard" className="mk-btn mk-btn--primary">
                    {locale === "zh-CN" ? "前往控制台配置" : "Configure in dashboard"}
                  </Link>
                )}
              </div>
            )}

            {state === "ok" && items.length > 0 && (
              <Table<DirectoryItem>
                className="mk-table"
                dataSource={filtered}
                columns={columns}
                rowKey="model_name"
                size="small"
                pagination={{ pageSize: 30, showSizeChanger: false, hideOnSinglePage: true }}
                locale={{
                  emptyText: locale === "zh-CN" ? "没有匹配的模型" : "No matching models",
                }}
              />
            )}
          </div>
        </div>

        <MarketingFooter />
      </div>
    </ConfigProvider>
  );
}
